import {
  Card,
  CardMedia,
  Typography,
  Box,
  useTheme,
  useMediaQuery,
  SxProps
} from '@mui/material';
import {Variant} from '@mui/material/styles/createTypography';
import {useTranslation} from 'react-i18next';

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    h4: true;
    h6: true;
  }
}

interface CardMediaComponentProps {
  image: string;
  altText: string;
  height?: string | number;
  sport?: string;
  online?: boolean;
  distance?: number;
  showSport?: boolean;
  showOnlineStatus?: boolean;
  sportNameSize?: 'h4' | 'h6';
  overlayLabel?: string;
  overlayLabelSize?: 'h4' | 'h5' | 'h6';
  gradient?: boolean;
  underlineOnHover?: boolean;
  eventCancelled?: boolean;
  pastEvent?: boolean;
  width?: string | number;
  objectFit?: 'fill' | 'contain' | 'cover' | 'none' | 'scale-down';
  sx?: SxProps;
}

export const CardMediaComponent: React.FC<CardMediaComponentProps> = ({
  image,
  altText,
  height = '240',
  sport,
  online,
  distance,
  showSport = false,
  showOnlineStatus = false,
  sportNameSize = 'h4',
  overlayLabelSize = 'h6',
  overlayLabel,
  gradient = false,
  underlineOnHover = false,
  eventCancelled = false,
  pastEvent = false,
  width = 'auto',
  objectFit,
  sx
}) => {
  const {t} = useTranslation();

  const theme = useTheme();
  const isSmScreen = useMediaQuery(theme.breakpoints.down('sm'));

  let variant: Variant;
  if (isSmScreen) {
    variant = 'h6';
  } else {
    variant = overlayLabelSize;
  }

  let displayDistance;

  if (distance !== undefined) {
    if (distance < 1) {
      displayDistance = `${t('event_details_less_than_one')} km`;
    } else {
      displayDistance = `${distance.toFixed(1)} km`;
    }
  }

  return (
    <Card
      sx={{
        position: 'relative',
        opacity: eventCancelled || pastEvent ? 0.4 : 1,
        overflow: 'hidden',
        width: width,
        '&:hover .MuiTypography-root': underlineOnHover
          ? {textDecoration: 'underline #ddd'}
          : {},
        ...sx
      }}
    >
      <CardMedia
        component="img"
        height={height}
        image={image}
        alt={altText}
        sx={{
          objectFit: objectFit ? objectFit : 'cover'
        }}
      />
      {gradient && (
        <div
          style={{
            content: '""',
            display: 'block',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background:
              'linear-gradient(to top, transparent 50%, rgba(0, 0, 0, 0.5))'
          }}
        >
          &nbsp;
        </div>
      )}
      {/* text overlaying the background image */}
      {overlayLabel && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 3,
            p: 1,
            //backgroundColor: 'rgba(0, 0, 0, 0.5)',
            borderRadius: 1,
            width: '100%',
            textAlign: 'left'
          }}
        >
          <Typography
            variant={variant}
            sx={{
              color: '#ddd',
              textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)'
            }}
          >
            {overlayLabel}
          </Typography>
        </Box>
      )}
      {showSport && sport && (
        <Box sx={{position: 'absolute', top: 2, left: 2, p: 1}}>
          <Typography
            variant={sportNameSize}
            sx={{color: 'rgba(255, 255, 255, 0.75)'}}
          >
            {sport}
          </Typography>
        </Box>
      )}
      {distance !== undefined && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            p: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            borderRadius: 1
          }}
        >
          <Typography variant="h6" sx={{color: 'rgba(255, 255, 255, 0.85)'}}>
            {displayDistance}
          </Typography>
        </Box>
      )}
      {showOnlineStatus && online && (
        <Box sx={{position: 'absolute', bottom: 2, right: 2, p: 1}}>
          <Typography variant="h6" sx={{color: 'rgba(255, 255, 255, 0.85)'}}>
            {t('online_big')}
          </Typography>
        </Box>
      )}
    </Card>
  );
};
